import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, calculatePolygonArea, handleFileDrop } from '../services/mapService';
import { fetchCommuneBoundaries } from '../services/communeBoundaryService';
import './FieldMap.css';

const LIBRARIES = ['geometry'];
const DEFAULT_CENTER = { lat: 47.056414, lng: 26.846064 };
const LABEL_MIN_ZOOM = 14;
const LABEL_LIMIT = 120;
const MIDPOINT_VERTEX_LIMIT = 120;

export const CROP_COLORS = {
  wheat: '#E8A838', corn: '#3B82F6', sunflower: '#FACC15', rapeseed: '#84CC16',
  alfalfa: '#A855F7', soybean: '#F97316', fallow: '#94A3B8',
};

const baseStyle = (meta) => {
  if (meta.imported) return { fillColor: '#F39C12', fillOpacity: 0.4, strokeColor: '#E67E22', strokeWeight: 1.5, strokeOpacity: 1 };
  if (meta.apiaOnly) return { fillColor: '#E53935', fillOpacity: 0.25, strokeColor: '#B71C1C', strokeWeight: 1.5, strokeOpacity: 0.8 };
  if (meta.folosinta === 'pasune') return { fillColor: '#2D6A2D', fillOpacity: 0.45, strokeColor: '#1B4D1B', strokeWeight: 1.5, strokeOpacity: 1 };
  if (!meta.cropType) return { fillColor: '#FFFFFF', fillOpacity: 0.3, strokeColor: '#FFFFFF', strokeWeight: 1.5, strokeOpacity: 0.85 };
  return { fillColor: CROP_COLORS[meta.cropType] || '#2ECC71', fillOpacity: 0.48, strokeColor: '#FFFFFF', strokeWeight: 1.5, strokeOpacity: 0.85 };
};

// The map survives leaving and re-entering the page, so tiles and fields don't reload each time.
let shared = null;

const createLabelLayer = (g, map) => {
  class LabelLayer extends g.OverlayView {
    constructor() { super(); this.items = new Map(); this.root = document.createElement('div'); this.root.className = 'fm-labels'; }
    onAdd() { this.getPanes().overlayLayer.appendChild(this.root); }
    onRemove() { this.root.remove(); }
    draw() {
      const projection = this.getProjection();
      if (!projection) return;
      this.items.forEach(({ element, latLng }) => {
        const point = projection.fromLatLngToDivPixel(latLng);
        element.style.transform = `translate(${Math.round(point.x)}px, ${Math.round(point.y)}px) translate(-50%, -50%)`;
      });
    }
    show(list) {
      const keep = new Set(list.map((item) => item.id));
      this.items.forEach((item, id) => { if (!keep.has(id)) { item.element.remove(); this.items.delete(id); } });
      list.forEach((item) => {
        const existing = this.items.get(item.id);
        if (existing) { if (existing.element.textContent !== item.name) existing.element.textContent = item.name; existing.latLng = item.latLng; return; }
        const element = document.createElement('div');
        element.className = 'fm-label';
        element.textContent = item.name;
        this.root.appendChild(element);
        this.items.set(item.id, { element, latLng: item.latLng });
      });
      this.draw();
    }
  }
  const layer = new LabelLayer();
  layer.setMap(map);
  return layer;
};

const getShared = (g) => {
  if (shared) return shared;
  const host = document.createElement('div');
  host.className = 'fm-canvas';
  const map = new g.Map(host, {
    center: DEFAULT_CENTER, zoom: 13, mapTypeId: 'satellite', tilt: 0, gestureHandling: 'greedy',
    disableDefaultUI: true, zoomControl: true, scaleControl: true, clickableIcons: false,
    zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
  });
  const boundaries = new g.Data({ map });
  boundaries.setStyle({ fillOpacity: 0, strokeColor: '#FF3B30', strokeWeight: 2.5, strokeOpacity: 0.9, clickable: false, zIndex: 50 });
  shared = { host, map, boundaries, index: new Map(), labels: createLabelLayer(g, map), fitted: false };
  return shared;
};

const toLatLngs = (g, coordinates) => coordinates.map((p) => new g.LatLng(p.lat, p.lng));

// Same shape gives the same key, whatever order Firebase returns lat and lng in.
const shapeKey = (coordinates) => coordinates.map((p) => `${p.lat},${p.lng}`).join(';');

const centerOf = (coordinates) => ({
  lat: coordinates.reduce((sum, p) => sum + p.lat, 0) / coordinates.length,
  lng: coordinates.reduce((sum, p) => sum + p.lng, 0) / coordinates.length,
});

const FieldMap = forwardRef(({
  fields, importedPolygons, selectedId, dimmedIds, hiddenId, alertId,
  onSelect, onBackgroundClick, onImportKML, onSessionChange, onMapReady, chromeOffset,
}, ref) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'farmtrack-google-maps', googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: LIBRARIES, language: 'ro', region: 'RO',
  });
  const containerRef = useRef(null);
  const handlers = useRef({});
  handlers.current = { onSelect, onBackgroundClick, onImportKML, onSessionChange };
  const latestFields = useRef(fields);
  latestFields.current = fields;

  const appliedSelection = useRef({});
  const dimmedRef = useRef(dimmedIds);
  const sessionRef = useRef(null);
  const locationMarker = useRef(null);
  const [ready, setReady] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [mapType, setMapType] = useState('satellite');
  const [layersOpen, setLayersOpen] = useState(false);
  const [boundariesOn, setBoundariesOn] = useState(false);
  const [boundariesProgress, setBoundariesProgress] = useState(null);
  const boundariesRun = useRef(0);
  const [sessionMode, setSessionMode] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    window.gm_authFailure = () => setAuthFailed(true);
    return () => { delete window.gm_authFailure; };
  }, []);

  const refreshLabels = () => {
    const { map, index, labels } = shared;
    labels.root.style.display = '';
    const zoom = map.getZoom();
    const bounds = map.getBounds();
    if (!bounds || zoom < LABEL_MIN_ZOOM) { labels.show([]); return; }
    const visible = [];
    index.forEach((entry, id) => {
      if (!entry.name) return;
      if (!bounds.intersects(entry.bounds)) return;
      visible.push({ id, name: entry.name, latLng: entry.center, area: entry.size });
    });
    visible.sort((a, b) => b.area - a.area);
    labels.show(visible.slice(0, LABEL_LIMIT));
  };

  // ── Attach the map to this page ──────────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !containerRef.current) return undefined;
    const g = window.google.maps;
    const state = getShared(g);
    const { host, map } = state;
    containerRef.current.prepend(host);

    map.data.setStyle((feature) => {
      const meta = feature.getProperty('meta') || {};
      const style = baseStyle(meta);
      if (dimmedRef.current?.has(meta.id)) {
        return { ...style, fillOpacity: 0.08, strokeOpacity: 0.35, strokeWeight: 1 };
      }
      return style;
    });

    const listeners = [
      map.data.addListener('click', (event) => {
        if (sessionRef.current) return;
        handlers.current.onSelect?.(event.feature.getProperty('meta').id);
      }),
      map.addListener('click', () => {
        if (sessionRef.current) return;
        handlers.current.onBackgroundClick?.();
      }),
      map.addListener('zoom_changed', () => { state.labels.root.style.display = 'none'; }),
      map.addListener('idle', refreshLabels),
    ];

    // While drawing, taps are read from the screen directly: Google Maps drops a tap that
    // comes quickly after another, and every tap must place a point.
    let press = null;
    const onPointerDown = (event) => {
      press = sessionRef.current && event.isPrimary ? { x: event.clientX, y: event.clientY, at: Date.now(), multi: false } : null;
    };
    const onPointerMove = (event) => { if (!event.isPrimary && press) press.multi = true; };
    const onPointerUp = (event) => {
      const session = sessionRef.current;
      const start = press;
      press = null;
      if (!session || session.mode === 'shape' || !start || start.multi || !event.isPrimary) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8 || Date.now() - start.at > 600) return;
      const projection = state.labels.getProjection();
      if (!projection) return;
      const rect = host.getBoundingClientRect();
      const pixel = new g.Point(event.clientX - rect.left, event.clientY - rect.top);
      // A tap on an existing point selects or moves that point instead.
      const onHandle = [...session.markers, ...session.mids].some((marker) => {
        const p = projection.fromLatLngToContainerPixel(marker.getPosition());
        return Math.hypot(p.x - pixel.x, p.y - pixel.y) < (window.matchMedia('(pointer: coarse)').matches ? 20 : 12);
      });
      if (!onHandle) addPoint(projection.fromContainerPixelToLatLng(pixel));
    };
    host.addEventListener('pointerdown', onPointerDown, true);
    host.addEventListener('pointermove', onPointerMove, true);
    host.addEventListener('pointerup', onPointerUp, true);

    setReady(true);
    onMapReady?.(map);
    return () => {
      host.removeEventListener('pointerdown', onPointerDown, true);
      host.removeEventListener('pointermove', onPointerMove, true);
      host.removeEventListener('pointerup', onPointerUp, true);
      listeners.forEach((listener) => listener.remove());
      endSession();
      host.remove();
    };
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep the field layer in step, touching only what changed ─────────────────
  useEffect(() => {
    if (!ready || !shared) return;
    const g = window.google.maps;
    const { map, index } = shared;
    const seen = new Set();

    const sync = (item, imported) => {
      if (!item.coordinates || item.coordinates.length < 3) return;
      const key = String(item.id);
      seen.add(key);
      const meta = {
        id: item.id, imported, name: item.name || '',
        cropType: item.cropType || '', folosinta: item.folosinta || '', apiaOnly: Boolean(item.apiaOnly),
      };
      const metaKey = JSON.stringify(meta);
      const entry = index.get(key);

      if (!entry) {
        const feature = map.data.add(new g.Data.Feature({
          id: key,
          geometry: new g.Data.Polygon([toLatLngs(g, item.coordinates)]),
          properties: { meta },
        }));
        index.set(key, makeEntry(g, item, feature, metaKey));
        return;
      }

      if (entry.coordinatesRef !== item.coordinates) {
        const signature = shapeKey(item.coordinates);
        if (signature !== entry.signature) {
          entry.feature.setGeometry(new g.Data.Polygon([toLatLngs(g, item.coordinates)]));
          Object.assign(entry, makeEntry(g, item, entry.feature, metaKey), { signature });
        }
        entry.coordinatesRef = item.coordinates;
      }
      if (entry.metaKey !== metaKey) {
        entry.feature.setProperty('meta', meta);
        entry.metaKey = metaKey;
        entry.name = meta.name;
      }
    };

    (fields || []).forEach((item) => sync(item, false));
    (importedPolygons || []).forEach((item) => sync(item, true));

    index.forEach((entry, key) => {
      if (!seen.has(key)) { map.data.remove(entry.feature); index.delete(key); }
    });

    if (!shared.fitted && index.size > 0) {
      shared.fitted = true;
      const bounds = new g.LatLngBounds();
      index.forEach((entry) => bounds.union(entry.bounds));
      map.fitBounds(bounds, 60);
    }
    refreshLabels();
  }, [ready, fields, importedPolygons]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Selection, split target and hidden field: one feature at a time ──────────
  useEffect(() => {
    if (!ready || !shared) return;
    const { map, index } = shared;
    const entryFor = (id) => (id === null || id === undefined ? null : index.get(String(id)));
    const previous = appliedSelection.current;
    [previous.selectedId, previous.hiddenId, previous.alertId].forEach((id) => {
      const entry = entryFor(id);
      if (entry) map.data.revertStyle(entry.feature);
    });
    const selected = entryFor(selectedId);
    if (selected) map.data.overrideStyle(selected.feature, { strokeColor: '#C8F04B', strokeWeight: 3.5, strokeOpacity: 1, zIndex: 10 });
    const alert = entryFor(alertId);
    if (alert) map.data.overrideStyle(alert.feature, { fillColor: '#FF3333', fillOpacity: 0.22, strokeColor: '#CC0000', strokeWeight: 3, strokeOpacity: 1 });
    const hidden = entryFor(hiddenId);
    if (hidden) map.data.overrideStyle(hidden.feature, { visible: false });
    appliedSelection.current = { selectedId, hiddenId, alertId };
  }, [ready, selectedId, hiddenId, alertId, fields]);

  // ── Filters dim the fields that don't match ──────────────────────────────────
  useEffect(() => {
    dimmedRef.current = dimmedIds;
    if (!ready || !shared) return;
    const { map } = shared;
    map.data.setStyle(map.data.getStyle());
  }, [ready, dimmedIds]);

  // ── Shape editor, shared by drawing, editing and splitting ───────────────────
  const notifySession = () => {
    const session = sessionRef.current;
    if (!session) { handlers.current.onSessionChange?.(null); return; }
    handlers.current.onSessionChange?.({
      mode: session.mode, points: session.path.length,
      area: session.path.length >= 3 ? calculatePolygonArea(session.path) : 0,
      canUndo: session.history.length > 0, hasSelectedPoint: session.selectedVertex !== null && session.path.length > 3,
      dirty: session.dirty,
    });
  };

  const snapshot = () => {
    const session = sessionRef.current;
    session.history.push(session.path.map((p) => ({ ...p })));
    if (session.history.length > 100) session.history.shift();
    session.dirty = true;
  };

  const vertexIcon = (g, selected, first) => ({
    path: g.SymbolPath.CIRCLE, scale: window.matchMedia('(pointer: coarse)').matches ? 11 : 7,
    fillColor: selected ? '#C8F04B' : first ? '#14432F' : '#FFFFFF', fillOpacity: 1, strokeColor: first ? '#FFFFFF' : '#14432F', strokeWeight: 3,
  });

  const redrawSession = () => {
    const session = sessionRef.current;
    if (!session) return;
    const g = window.google.maps;
    const { map } = shared;
    session.polygon.setPath(toLatLngs(g, session.path));
    session.markers.forEach((marker) => marker.setMap(null));
    session.markers = [];

    session.path.forEach((point, i) => {
      const marker = new g.Marker({
        position: point, map, draggable: true, zIndex: 1000,
        icon: vertexIcon(g, session.selectedVertex === i, i === 0),
      });
      marker.addListener('dragstart', () => { snapshot(); session.mids.forEach((mid) => mid.setVisible(false)); });
      marker.addListener('drag', (event) => {
        session.path[i] = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        session.polygon.getPath().setAt(i, event.latLng);
        scheduleNotify();
      });
      marker.addListener('dragend', () => redrawSession());
      marker.addListener('click', () => {
        session.selectedVertex = session.selectedVertex === i ? null : i;
        redrawSession();
      });
      session.markers.push(marker);
    });

    session.mids.forEach((mid) => mid.setMap(null));
    session.mids = [];
    if (session.path.length >= 3 && session.path.length <= MIDPOINT_VERTEX_LIMIT) {
      session.path.forEach((point, i) => {
        const next = session.path[(i + 1) % session.path.length];
        const mid = new g.Marker({
          position: { lat: (point.lat + next.lat) / 2, lng: (point.lng + next.lng) / 2 }, map, draggable: true, zIndex: 900,
          icon: { path: g.SymbolPath.CIRCLE, scale: window.matchMedia('(pointer: coarse)').matches ? 8 : 5, fillColor: '#C8F04B', fillOpacity: 0.95, strokeColor: '#14432F', strokeWeight: 2 },
        });
        let inserted = false;
        const insert = (latLng) => {
          snapshot();
          session.path.splice(i + 1, 0, { lat: latLng.lat(), lng: latLng.lng() });
          session.polygon.getPath().insertAt(i + 1, latLng);
          inserted = true;
        };
        mid.addListener('dragstart', (event) => insert(event.latLng));
        mid.addListener('drag', (event) => {
          if (!inserted) return;
          session.path[i + 1] = { lat: event.latLng.lat(), lng: event.latLng.lng() };
          session.polygon.getPath().setAt(i + 1, event.latLng);
          scheduleNotify();
        });
        mid.addListener('dragend', () => redrawSession());
        mid.addListener('click', (event) => { insert(event.latLng); redrawSession(); });
        session.mids.push(mid);
      });
    }
    notifySession();
  };

  const scheduleNotify = () => {
    const session = sessionRef.current;
    if (!session || session.frame) return;
    session.frame = requestAnimationFrame(() => { if (sessionRef.current) sessionRef.current.frame = null; notifySession(); });
  };

  function addPoint(latLng) {
    const session = sessionRef.current;
    if (!session || session.mode === 'shape') return;
    snapshot();
    session.path.push({ lat: latLng.lat(), lng: latLng.lng() });
    session.selectedVertex = null;
    redrawSession();
  }

  function endSession() {
    const session = sessionRef.current;
    if (!session) return;
    if (session.frame) cancelAnimationFrame(session.frame);
    session.markers.forEach((marker) => marker.setMap(null));
    session.mids.forEach((mid) => mid.setMap(null));
    session.polygon.setMap(null);
    shared?.map.setOptions({ disableDoubleClickZoom: false });
    sessionRef.current = null;
    setSessionMode(null);
    notifySession();
  }

  const focusBounds = (bounds) => {
    const { map } = shared;
    const height = containerRef.current?.clientHeight || 600;
    const phone = window.matchMedia('(max-width: 900px)').matches;
    map.fitBounds(bounds, { top: 130, left: 40, right: 60, bottom: phone ? Math.round(height * 0.52) : 50 });
  };

  useImperativeHandle(ref, () => ({
    focusField(field) {
      if (!ready || !field?.coordinates || field.coordinates.length < 3) return;
      const g = window.google.maps;
      const bounds = new g.LatLngBounds();
      toLatLngs(g, field.coordinates).forEach((latLng) => bounds.extend(latLng));
      focusBounds(bounds);
    },
    startSession(mode, path = []) {
      if (!ready) return;
      endSession();
      const g = window.google.maps;
      sessionRef.current = {
        mode, path: path.map((p) => ({ lat: p.lat, lng: p.lng })), history: [], dirty: false,
        selectedVertex: null, markers: [], mids: [], frame: null,
        polygon: new g.Polygon({
          map: shared.map, clickable: false, zIndex: 800,
          fillColor: mode === 'split' ? '#FF3333' : '#C8F04B', fillOpacity: 0.28,
          strokeColor: mode === 'split' ? '#FF3333' : '#C8F04B', strokeWeight: 3,
        }),
      };
      // Quick taps must each place a point, not count as a double-tap zoom.
      shared.map.setOptions({ disableDoubleClickZoom: true });
      setSessionMode(mode);
      setLayersOpen(false);
      redrawSession();
    },
    addPointAtCenter() { const center = shared?.map.getCenter(); if (center) addPoint(center); },
    undo() {
      const session = sessionRef.current;
      if (!session?.history.length) return;
      session.path = session.history.pop();
      session.selectedVertex = null;
      redrawSession();
    },
    clearPoints() {
      const session = sessionRef.current;
      if (!session?.path.length) return;
      snapshot();
      session.path = [];
      session.selectedVertex = null;
      redrawSession();
    },
    deleteSelectedPoint() {
      const session = sessionRef.current;
      if (!session || session.selectedVertex === null || session.path.length <= 3) return;
      snapshot();
      session.path.splice(session.selectedVertex, 1);
      session.selectedVertex = null;
      redrawSession();
    },
    getSessionPath() { return sessionRef.current ? sessionRef.current.path.map((p) => ({ ...p })) : []; },
    endSession,
  }), [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Map tools ────────────────────────────────────────────────────────────────
  const changeMapType = (type) => { setMapType(type); shared?.map.setMapTypeId(type); };

  const toggleBoundaries = async () => {
    if (!shared) return;
    const { boundaries } = shared;
    boundariesRun.current += 1;
    const run = boundariesRun.current;
    if (boundariesOn) {
      boundaries.forEach((feature) => boundaries.remove(feature));
      setBoundariesOn(false);
      setBoundariesProgress(null);
      return;
    }
    setBoundariesOn(true);
    setLayersOpen(false);
    const g = window.google.maps;
    const results = await fetchCommuneBoundaries(latestFields.current, {
      isCancelled: () => boundariesRun.current !== run,
      onProgress: (done, total) => setBoundariesProgress(done < total ? { done, total } : null),
      onBoundary: ({ rings }) => rings.forEach((ring) => {
        boundaries.add(new g.Data.Feature({ geometry: new g.Data.Polygon([toLatLngs(g, ring)]) }));
      }),
    });
    if (boundariesRun.current !== run) return;
    setBoundariesProgress(null);
    if (!results.length) {
      setBoundariesOn(false);
      setNotice('Nu s-au găsit limite. Verifică dacă câmpurile au comuna completată sau încearcă din nou.');
    }
  };

  const locate = () => {
    if (!navigator.geolocation) { setNotice('Acest dispozitiv nu oferă locația.'); return; }
    navigator.geolocation.getCurrentPosition((position) => {
      const g = window.google.maps;
      const location = { lat: position.coords.latitude, lng: position.coords.longitude };
      if (!locationMarker.current) {
        locationMarker.current = new g.Marker({
          map: shared.map, title: 'Locația mea', zIndex: 700, clickable: false,
          icon: { path: g.SymbolPath.CIRCLE, scale: 8, fillColor: '#4285F4', fillOpacity: 1, strokeColor: '#FFFFFF', strokeWeight: 2 },
        });
      }
      locationMarker.current.setPosition(location);
      shared.map.setCenter(location);
      shared.map.setZoom(Math.max(shared.map.getZoom(), sessionRef.current ? 18 : 16));
    }, () => setNotice('Nu am putut obține locația. Verifică permisiunea.'), { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 });
  };

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const onDrop = async (event) => {
    event.preventDefault();
    try {
      const polygons = await handleFileDrop(event);
      if (polygons.length) handlers.current.onImportKML?.(polygons);
      else setNotice('Fișierul KML nu conține poligoane valide.');
    } catch (error) {
      setNotice(error.message || 'Eroare la importul KML.');
    }
  };

  const failure = loadError || authFailed;

  return (
    <div className="fm" ref={containerRef} onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
      {!ready && !failure && <div className="fm-message">Se încarcă harta…</div>}
      {failure && (
        <div className="fm-message">
          Harta nu s-a putut încărca. Verifică internetul și cheia Google Maps.
          <button type="button" onClick={() => window.location.reload()}>Reîncarcă</button>
        </div>
      )}

      {(sessionMode === 'draw' || sessionMode === 'split') && (
        <div className="fm-cross" aria-hidden="true"><span /><span /></div>
      )}

      {ready && (
        <div className="fm-tools" style={chromeOffset ? { top: chromeOffset } : undefined}>
          <button type="button" aria-label="Straturi și limite comune" aria-expanded={layersOpen}
            className={layersOpen || boundariesOn ? 'is-on' : ''} onClick={() => setLayersOpen((open) => !open)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 3l9 5-9 5-9-5zM3 13l9 5 9-5" /></svg>
          </button>
          <button type="button" aria-label="Locația mea" onClick={locate}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 8a4 4 0 100 8 4 4 0 000-8zM12 2v4M12 18v4M2 12h4M18 12h4" /></svg>
          </button>
          {layersOpen && (
            <div className="fm-layers">
              <button type="button" className={mapType === 'satellite' ? 'is-on' : ''} onClick={() => changeMapType('satellite')}>Satelit</button>
              <button type="button" className={mapType === 'hybrid' ? 'is-on' : ''} onClick={() => changeMapType('hybrid')}>Satelit cu nume</button>
              <button type="button" className={mapType === 'roadmap' ? 'is-on' : ''} onClick={() => changeMapType('roadmap')}>Hartă</button>
              <button type="button" className={boundariesOn ? 'is-on' : ''} onClick={toggleBoundaries}>Limite comune</button>
            </div>
          )}
        </div>
      )}

      {boundariesProgress && (
        <div className="fm-notice" role="status">
          Se încarcă limitele comunelor: {boundariesProgress.done} din {boundariesProgress.total}. Prima dată poate dura câteva minute.
        </div>
      )}
      {notice && !boundariesProgress && <div className="fm-notice" role="status">{notice}</div>}
    </div>
  );
});

function makeEntry(g, item, feature, metaKey) {
  const bounds = new g.LatLngBounds();
  item.coordinates.forEach((p) => bounds.extend(p));
  const span = bounds.toSpan();
  return {
    id: String(item.id), feature, metaKey, bounds, name: item.name || '',
    center: new g.LatLng(centerOf(item.coordinates)), size: span.lat() * span.lng(),
    coordinatesRef: item.coordinates, signature: shapeKey(item.coordinates),
  };
}

export default React.memo(FieldMap);
